import dayjs from "dayjs";
import { useMemo, useState } from "react";
import {
  IoChevronBack,
  IoChevronForward, IoPeopleOutline
} from "react-icons/io5";
import { Cell, Pie, PieChart } from "recharts";

export default function PieChartForAttendance({ dbData, attendanceDate, onPrevious, onNext, onToday }) {
  const attendance = dbData?.[0];
  const [hovered, setHovered] = useState(null); // 'present' | 'absent' | null
  const isToday = attendanceDate === dayjs().format("YYYY-MM-DD");
  const chartData = useMemo(() => {
    if (!attendance) return [];
    return [
      { key: "present", name: "Present", value: attendance.presentMembersCount ?? 0, color: "#00CC00" },
      { key: "absent", name: "Absent", value: attendance.absentMembersCount ?? 0, color: "#bebebe" },
    ];
  }, [attendance]);

  if (!attendance) return null;

  const total = attendance.overallMemberCount ?? 0;
  const presentPct = total > 0 ? Math.round(((attendance.presentMembersCount ?? 0) / total) * 100) : 0;

  return (
    <div className="mt-0 flex h-full max-h-[660px] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg">
      {/* Header */}
      {/* <div className="mb-2 shrink-0">
        <h3 className="text-sm font-bold text-[#0B6BCB]">
          Today's Attendance <span className="font-medium text-gray-400">({attendance.date})</span>
        </h3>
        <p className="text-xs text-gray-400">Total: {total} members</p>
      </div> */}

      <div className="mb-2 flex items-center justify-between shrink-0">
        <div>
          <h3 className="text-sm font-bold text-[#0B6BCB]">
            Attendance
          </h3>
          <p className="text-xs text-gray-400">
            {attendanceDate} • Total: {total} members
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onPrevious}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 transition hover:bg-gray-100"
          >
            <IoChevronBack size={16} />
          </button>

          <button
            onClick={onToday}
            disabled={isToday}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition
    ${isToday
                ? "cursor-not-allowed bg-gray-200 text-gray-500"
                : "bg-[#0B6BCB] text-white hover:bg-[#0957a5]"
              }`}
          >
            Today
          </button>

          <button
            onClick={onNext}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 transition hover:bg-gray-100"
          >
            <IoChevronForward size={16} />
          </button>
        </div>
      </div>

      {/* Donut chart with center label */}
      <div className="relative flex shrink-0 items-center justify-center py-1" style={{ height: 200 }}>
        <PieChart width={200} height={200}>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx={100}
            cy={100}
            innerRadius={46}
            outerRadius={78}
            paddingAngle={3}
            cornerRadius={4}
            strokeWidth={0}
            isAnimationActive={true}
            onMouseEnter={(_, index) => setHovered(chartData[index].key)}
          >
            {chartData.map((entry) => (
              <Cell
                key={entry.key}
                fill={entry.color}
                opacity={hovered && hovered !== entry.key ? 0.35 : 1}
                style={{ transition: "opacity 0.15s ease", cursor: "pointer" }}
              />
            ))}
          </Pie>
        </PieChart>

        <div className="pointer-events-none absolute flex flex-col items-center">
          <span className="text-2xl font-extrabold text-gray-900">{presentPct}%</span>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Present</span>
        </div>
      </div>

      {/* Legend / summary chips — click to pin, not just hover */}
      <div className="mb-3 flex shrink-0 items-center justify-center gap-4">
        <button
          onClick={() => setHovered("present")}
          className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${hovered === "present" || !hovered ? "bg-green-50 text-green-700" : "text-gray-500"}`}
        >
          <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
          Present ({attendance.presentMembersCount ?? 0})
        </button>
        <button
          onClick={() => setHovered("absent")}
          className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${hovered === "absent" ? "bg-red-50 text-red-700" : "text-gray-500"}`}
        >
          <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
          Absent ({attendance.absentMembersCount ?? 0})
        </button>
      </div>

      {/* Member breakdown list — the min-h-0 is what makes internal scroll actually work */}
      <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50/50 p-3">
        {hovered === "absent" ? (
          <MemberList members={attendance.absentMembers} emptyLabel="No absent members" tone="red" />
        ) : (
          <MemberList members={attendance.presentMembers} emptyLabel="No present members" tone="green" />
        )}
      </div>
      <p className="mt-2 shrink-0 text-center text-[10px] text-gray-300">
        Click Present or Absent to switch the list above
      </p>
    </div>
  );
}

function MemberList({ members, emptyLabel, tone }) {
  const list = members ?? [];
  const dot = tone === "green" ? "bg-green-500" : "bg-red-500";

  if (list.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 py-4 text-gray-300">
        <IoPeopleOutline size={20} />
        <p className="text-xs font-medium">{emptyLabel}</p>
      </div>
    );
  }

  return (
    <ul className="space-y-1.5">
      {list.map((name, i) => (
        <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
          {name}
        </li>
      ))}
    </ul>
  );
}